import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Plus, Pencil, CheckCircle, Paperclip } from 'lucide-react';
import { Button, Badge, Modal, Input, EmptyState, toast } from '../../../ui';
import { LoadingSpinner } from '../../../components/common/LoadingSpinner';
import {
  useCompanies,
  useTransactions,
  useCategories,
  useBankAccounts,
  useCreateTransaction,
  useUpdateTransaction,
  useMarkTransactionPaid,
} from '../../../hooks/useFinancialOps';
import { AttachmentsDrawer } from '../components/AttachmentsDrawer';
import { ImportarExtratoButton } from '../components/ImportarExtratoButton';
import { ReconciliarDialog } from '../components/ReconciliarDialog';
import type { ReconciliarCandidate } from '../components/ReconciliarDialog';
import { formatCurrency, formatDateBR } from '../../../utils/format';
import { competenciaDe, competenciaDeslocada, competenciaLabel } from '../utils/competencia';

const STATUS_TONE: Record<string, 'neutral' | 'accent' | 'warning' | 'danger' | 'info'> = {
  PENDING: 'warning',
  PAID: 'accent',
  OVERDUE: 'danger',
  CANCELLED: 'neutral',
};

const STATUS_LABELS: Record<string, string> = {
  PENDING: 'Pendente',
  PAID: 'Pago',
  OVERDUE: 'Vencido',
  CANCELLED: 'Cancelado',
};

/** Só o que os seletores precisam — os hooks devolvem os registros inteiros. */
type CompanyOption = { id: string; name: string };
type BankAccountOption = { id: string; name: string; bankName: string };

/** Estado de rateio de uma linha, como o backend devolve na listagem. */
export type Intercompany =
  | { role: 'payer'; beneficiaryCompanyId: string; settled: boolean }
  | { role: 'beneficiary'; payerCompanyId: string; settled: boolean; fromCardInvoice: boolean }
  | null;

/**
 * Por que a linha não pode ser editada, ou null se pode. O backend recusa esses
 * casos com 409; dizer o motivo aqui evita o usuário abrir o modal para levar erro.
 */
export function motivoParaNaoEditar(ic: Intercompany): string | null {
  if (!ic) {
    return null;
  }
  if (ic.settled) {
    return 'Rateio já quitado — estorne a quitação no fechamento antes de editar.';
  }
  if (ic.role === 'beneficiary') {
    return ic.fromCardInvoice
      ? 'Despesa vinda de item de fatura de cartão — edite pela revisão da fatura.'
      : 'Perna espelho de um rateio — edite a transação da empresa que pagou.';
  }
  return null;
}

/**
 * Uma linha da lista de transações. `useTransactions` devolve `Record<string, any>`
 * (não há DTO de transação no frontend) — isto tipa só os campos que a tabela lê, para que
 * um campo renomeado ou ausente (ex.: `competenceDate`) quebre o typecheck em vez de
 * renderizar silenciosamente nada.
 */
type TransactionRow = {
  id: string;
  transactionDate: string;
  /** Mês em que a transação de fato entra na DRE — pode diferir de transactionDate
   *  quando o mês do fato já estava fechado (ver utils/competencia.ts). */
  competenceDate: string;
  description: string;
  cardInvoiceId?: string | null;
  category?: { name: string } | null;
  type: 'INCOME' | 'EXPENSE';
  amount: number;
  currency?: string;
  status: string;
  attachmentCount?: number;
  intercompany: Intercompany;
};


interface TxFormData {
  type: string;
  companyId: string;
  description: string;
  amount: string;
  transactionDate: string;
  categoryId: string;
  bankAccountId: string;
  /** Rateio: empresa que realmente incorreu na despesa. '' = esta empresa. */
  beneficiaryCompanyId: string;
  paymentMethod: string;
  notes: string;
}

const emptyForm = (companyId: string): TxFormData => ({
  type: 'INCOME',
  companyId,
  description: '',
  amount: '',
  transactionDate: new Date().toISOString().slice(0, 10),
  categoryId: '',
  bankAccountId: '',
  beneficiaryCompanyId: '',
  paymentMethod: '',
  notes: '',
});

export function TransacoesPage() {
  const { data: companiesData } = useCompanies({ active: true });
  const companies = companiesData?.data ?? [];

  const [selectedCompanyId, setSelectedCompanyId] = useState('');
  useEffect(() => {
    if (!selectedCompanyId && companies.length === 1) {
      setSelectedCompanyId(companies[0].id);
    }
  }, [companies, selectedCompanyId]);
  const [filterType, setFilterType] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterStart, setFilterStart] = useState('');
  const [filterEnd, setFilterEnd] = useState('');

  const params: Record<string, any> = { companyId: selectedCompanyId };
  if (filterType) {
    params.type = filterType;
  }
  if (filterStatus) {
    params.status = filterStatus;
  }
  if (filterStart) {
    params.startDate = filterStart;
  }
  if (filterEnd) {
    params.endDate = filterEnd;
  }

  const { data, isLoading } = useTransactions(params);
  const { data: categoriesData } = useCategories();
  const { data: bankAccountsData } = useBankAccounts(selectedCompanyId, { active: true });
  const createTx = useCreateTransaction();
  const updateTx = useUpdateTransaction();
  const markPaid = useMarkTransactionPaid();

  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState<TxFormData>(emptyForm(''));
  const [attachmentsTxId, setAttachmentsTxId] = useState<string | null>(null);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [mergeOpen, setMergeOpen] = useState(false);

  function toggleSelect(id: string) {
    const next = new Set(selectedIds);
    if (next.has(id)) {
      next.delete(id);
    } else {
      next.add(id);
    }
    setSelectedIds(next);
  }

  const transactions = data?.data ?? [];
  const categories = categoriesData?.data ?? [];
  const bankAccounts: BankAccountOption[] = bankAccountsData?.data ?? [];
  // Rateio é entre duas empresas: a pagadora nunca aparece como opção.
  const otherCompanies: CompanyOption[] = companies.filter(
    (c: CompanyOption) => c.id !== selectedCompanyId
  );

  if (companies.length === 0) {
    return (
      <EmptyState
        title="Cadastre uma empresa primeiro"
        body="Para registrar transações, primeiro cadastre ao menos uma empresa em Configuração → Financeiro → Empresas."
        action={
          <Link to="/config/financeiro/empresas">
            <Button size="sm">
              <Plus size={16} className="mr-1" /> Cadastrar Empresa
            </Button>
          </Link>
        }
      />
    );
  }

  function openCreate() {
    setEditingId(null);
    setForm(emptyForm(selectedCompanyId));
    setShowModal(true);
  }

  function openEdit(tx: any) {
    setEditingId(tx.id);
    setForm({
      type: tx.type,
      companyId: tx.companyId,
      description: tx.description ?? '',
      amount: String(tx.amount),
      transactionDate: tx.transactionDate?.slice(0, 10) ?? '',
      categoryId: tx.categoryId ?? '',
      bankAccountId: tx.bankAccountId ?? '',
      beneficiaryCompanyId:
        tx.intercompany?.role === 'payer' ? tx.intercompany.beneficiaryCompanyId : '',
      paymentMethod: tx.paymentMethod ?? '',
      notes: tx.notes ?? '',
    });
    setShowModal(true);
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    const payload: Record<string, any> = {
      ...form,
      amount: parseFloat(form.amount),
    };
    if (!payload.categoryId) {
      delete payload.categoryId;
    }
    if (!payload.bankAccountId) {
      delete payload.bankAccountId;
    }
    if (!payload.paymentMethod) {
      delete payload.paymentMethod;
    }
    if (!payload.notes) {
      delete payload.notes;
    }
    // Na criação, '' não é rateio e o campo sai do payload. Na edição ele vai
    // mesmo vazio: '' é como se desmarca um rateio existente.
    if (!editingId && !payload.beneficiaryCompanyId) {
      delete payload.beneficiaryCompanyId;
    }

    try {
      if (editingId) {
        await updateTx.mutateAsync({ id: editingId, data: payload });
        toast.success('Transação atualizada');
      } else {
        await createTx.mutateAsync(payload);
        toast.success('Transação criada');
      }
      setShowModal(false);
    } catch (err: any) {
      const msg =
        err?.response?.data?.details?.[0]?.message ||
        err?.response?.data?.message ||
        err?.message ||
        'Erro ao salvar transação';
      toast.error(msg);
    }
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end gap-3">
        <div>
          <label className="block text-xs font-medium text-ink-secondary uppercase tracking-wider mb-1.5">
            Empresa
          </label>
          <select
            className="h-9 border border-border rounded-md px-3 text-sm bg-surface text-ink min-w-[200px]"
            value={selectedCompanyId}
            onChange={(e) => setSelectedCompanyId(e.target.value)}
          >
            <option value="">Selecione...</option>
            {companies.map((c: any) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-xs font-medium text-ink-secondary uppercase tracking-wider mb-1.5">
            Tipo
          </label>
          <select
            className="h-9 border border-border rounded-md px-3 text-sm bg-surface text-ink"
            value={filterType}
            onChange={(e) => setFilterType(e.target.value)}
          >
            <option value="">Todos</option>
            <option value="INCOME">Receita</option>
            <option value="EXPENSE">Despesa</option>
          </select>
        </div>
        <div>
          <label className="block text-xs font-medium text-ink-secondary uppercase tracking-wider mb-1.5">
            Status
          </label>
          <select
            className="h-9 border border-border rounded-md px-3 text-sm bg-surface text-ink"
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
          >
            <option value="">Todos</option>
            <option value="PENDING">Pendente</option>
            <option value="PAID">Pago</option>
            <option value="OVERDUE">Vencido</option>
            <option value="CANCELLED">Cancelado</option>
          </select>
        </div>
        <Input
          label="De"
          type="date"
          value={filterStart}
          onChange={(e) => setFilterStart(e.target.value)}
        />
        <Input
          label="Até"
          type="date"
          value={filterEnd}
          onChange={(e) => setFilterEnd(e.target.value)}
        />
        {selectedCompanyId && (
          <>
            <ImportarExtratoButton companyId={selectedCompanyId} />
            <Button
              variant="ghost"
              size="sm"
              disabled={selectedIds.size < 2}
              onClick={() => setMergeOpen(true)}
            >
              Mesclar {selectedIds.size > 0 ? `(${selectedIds.size})` : ''}
            </Button>
            <Button onClick={openCreate} size="sm">
              <Plus size={16} className="mr-1" /> Nova Transação
            </Button>
          </>
        )}
      </div>

      {!selectedCompanyId ? (
        <EmptyState
          title="Selecione uma empresa"
          body="Escolha uma empresa para ver as transações."
        />
      ) : isLoading ? (
        <div className="flex justify-center py-12">
          <LoadingSpinner size="lg" />
        </div>
      ) : transactions.length === 0 ? (
        <EmptyState
          title="Nenhuma transação"
          body="Nenhuma transação encontrada para os filtros selecionados."
        />
      ) : (
        <div className="border border-border rounded-lg overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-subtle text-ink-secondary">
              <tr>
                <th className="w-8"></th>
                <th className="px-4 py-2 text-left font-medium">Data</th>
                <th className="px-4 py-2 text-left font-medium">Descrição</th>
                <th className="px-4 py-2 text-left font-medium">Categoria</th>
                <th className="px-4 py-2 text-left font-medium">Tipo</th>
                <th className="px-4 py-2 text-right font-medium">Valor</th>
                <th className="px-4 py-2 text-left font-medium">Status</th>
                <th className="px-4 py-2 text-left font-medium">Ações</th>
              </tr>
            </thead>
            <tbody>
              {transactions.map((tx: TransactionRow) => (
                <tr key={tx.id} className="border-t border-border hover:bg-subtle/50">
                  <td className="w-8 px-2">
                    <input
                      type="checkbox"
                      checked={selectedIds.has(tx.id)}
                      onChange={() => toggleSelect(tx.id)}
                    />
                  </td>
                  <td className="px-4 py-3 text-ink-secondary">
                    {formatDateBR(tx.transactionDate)}
                    {competenciaDeslocada(tx) && (
                      <span
                        className="ml-2 text-xs opacity-70"
                        title="Competência deslocada: o mês do fato já estava fechado"
                      >
                        competência {competenciaLabel(competenciaDe(tx.competenceDate))}
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-ink">
                    {tx.description}
                    {tx.cardInvoiceId && (
                      <span className="ml-2 inline-block align-middle">
                        <Badge tone="info">Fatura de cartão</Badge>
                      </span>
                    )}
                    {tx.intercompany && (
                      <span className="ml-2 inline-block align-middle">
                        <Badge tone={tx.intercompany.settled ? 'accent' : 'warning'}>
                          {tx.intercompany.role === 'payer'
                            ? tx.intercompany.settled
                              ? 'Rateio quitado'
                              : 'Rateio a receber'
                            : 'Rateio de outra empresa'}
                        </Badge>
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-ink-secondary">{tx.category?.name ?? '—'}</td>
                  <td className="px-4 py-3">
                    <Badge tone={tx.type === 'INCOME' ? 'accent' : 'danger'}>
                      {tx.type === 'INCOME' ? 'Receita' : 'Despesa'}
                    </Badge>
                  </td>
                  <td className="px-4 py-3 text-right font-medium text-ink">
                    {formatCurrency(tx.amount, tx.currency)}
                  </td>
                  <td className="px-4 py-3">
                    <Badge tone={STATUS_TONE[tx.status] ?? 'neutral'}>
                      {STATUS_LABELS[tx.status] ?? tx.status}
                    </Badge>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex gap-2">
                      <button
                        onClick={() => setAttachmentsTxId(tx.id)}
                        className="text-ink-secondary hover:text-ink-primary flex items-center gap-1"
                        title="Anexos"
                      >
                        <Paperclip size={16} />
                        {(tx.attachmentCount ?? 0) > 0 && (
                          <span className="text-xs">{tx.attachmentCount}</span>
                        )}
                      </button>
                      {tx.status === 'PENDING' && (
                        <button
                          onClick={() => markPaid.mutate({ id: tx.id })}
                          className="text-ink-secondary hover:text-accent"
                          title="Marcar como pago"
                        >
                          <CheckCircle size={16} />
                        </button>
                      )}
                      <button
                        onClick={() => openEdit(tx)}
                        className="text-ink-secondary hover:text-ink-primary disabled:opacity-40 disabled:hover:text-ink-secondary"
                        disabled={motivoParaNaoEditar(tx.intercompany) !== null}
                        title={motivoParaNaoEditar(tx.intercompany) ?? 'Editar'}
                      >
                        <Pencil size={16} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Modal
        open={showModal}
        onClose={() => setShowModal(false)}
        title={editingId ? 'Editar Transação' : 'Nova Transação'}
      >
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-xs font-medium text-ink-secondary uppercase tracking-wider mb-1.5">
              Tipo *
            </label>
            <select
              className="h-9 w-full border border-border rounded-md px-3 text-sm bg-surface text-ink"
              value={form.type}
              onChange={(e) =>
                setForm((f) => ({
                  ...f,
                  type: e.target.value,
                  // Rateio só existe em despesa; trocar para receita o descarta.
                  beneficiaryCompanyId: e.target.value === 'EXPENSE' ? f.beneficiaryCompanyId : '',
                }))
              }
            >
              <option value="INCOME">Receita</option>
              <option value="EXPENSE">Despesa</option>
            </select>
          </div>
          <Input
            label="Descrição *"
            value={form.description}
            onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
            required
          />
          <Input
            label="Valor *"
            type="number"
            step="0.01"
            min="0.01"
            value={form.amount}
            onChange={(e) => setForm((f) => ({ ...f, amount: e.target.value }))}
            required
          />
          <Input
            label="Data *"
            type="date"
            value={form.transactionDate}
            onChange={(e) => setForm((f) => ({ ...f, transactionDate: e.target.value }))}
            required
          />
          <div>
            <label className="block text-xs font-medium text-ink-secondary uppercase tracking-wider mb-1.5">
              Categoria
            </label>
            <select
              className="h-9 w-full border border-border rounded-md px-3 text-sm bg-surface text-ink"
              value={form.categoryId}
              onChange={(e) => setForm((f) => ({ ...f, categoryId: e.target.value }))}
            >
              <option value="">Sem categoria</option>
              {categories
                .filter((c: any) => c.type === form.type && c.active)
                .map((c: any) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-ink-secondary uppercase tracking-wider mb-1.5">
              Conta Bancária
            </label>
            <select
              className="h-9 w-full border border-border rounded-md px-3 text-sm bg-surface text-ink"
              value={form.bankAccountId}
              onChange={(e) => setForm((f) => ({ ...f, bankAccountId: e.target.value }))}
            >
              <option value="">Sem conta</option>
              {bankAccounts.map((a) => (
                <option key={a.id} value={a.id}>
                  {a.bankName} — {a.name}
                </option>
              ))}
            </select>
          </div>
          {form.type === 'EXPENSE' && otherCompanies.length > 0 && (
            <div>
              <label className="block text-xs font-medium text-ink-secondary uppercase tracking-wider mb-1.5">
                Gasto de outra empresa
              </label>
              <select
                className="h-9 w-full border border-border rounded-md px-3 text-sm bg-surface text-ink disabled:opacity-50"
                value={form.beneficiaryCompanyId}
                onChange={(e) => setForm((f) => ({ ...f, beneficiaryCompanyId: e.target.value }))}
                disabled={!form.bankAccountId || !form.categoryId}
                title={
                  !form.bankAccountId || !form.categoryId
                    ? 'Rateio exige conta bancária e categoria.'
                    : undefined
                }
              >
                <option value="">Esta empresa</option>
                {otherCompanies.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
              </select>
              <p className="mt-1 text-xs text-ink-tertiary">
                {!form.bankAccountId || !form.categoryId
                  ? 'Para ratear, escolha a conta bancária que pagou e a categoria da despesa.'
                  : editingId
                    ? 'Trocar a empresa move a despesa de DRE. Voltar para "Esta empresa" desfaz o rateio e devolve a despesa para cá.'
                    : 'A despesa vai para a DRE da empresa escolhida; o dinheiro continua saindo da conta desta. O saldo é quitado no fechamento do mês.'}
              </p>
            </div>
          )}
          <div>
            <label className="block text-xs font-medium text-ink-secondary uppercase tracking-wider mb-1.5">
              Forma de Pagamento
            </label>
            <select
              className="h-9 w-full border border-border rounded-md px-3 text-sm bg-surface text-ink"
              value={form.paymentMethod}
              onChange={(e) => setForm((f) => ({ ...f, paymentMethod: e.target.value }))}
            >
              <option value="">—</option>
              {['PIX', 'BOLETO', 'TED', 'DOC', 'CREDIT_CARD', 'DEBIT_CARD', 'CASH', 'OTHER'].map(
                (m) => (
                  <option key={m} value={m}>
                    {m}
                  </option>
                )
              )}
            </select>
          </div>
          <Input
            label="Observações"
            value={form.notes}
            onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))}
          />
          <div className="flex justify-end gap-3 pt-2">
            <Button type="button" variant="ghost" onClick={() => setShowModal(false)}>
              Cancelar
            </Button>
            <Button type="submit" loading={createTx.isPending || updateTx.isPending}>
              {editingId ? 'Salvar' : 'Criar'}
            </Button>
          </div>
        </form>
      </Modal>

      <AttachmentsDrawer transactionId={attachmentsTxId} onClose={() => setAttachmentsTxId(null)} />

      {mergeOpen && (
        <ReconciliarDialog
          open={mergeOpen}
          onClose={() => setMergeOpen(false)}
          candidates={transactions
            .filter((t: any) => selectedIds.has(t.id))
            .map(
              (t: any): ReconciliarCandidate => ({
                id: t.id,
                description: t.description,
                amount: Number(t.amount),
                type: t.type,
                transactionDate: t.transactionDate,
                categoryId: t.categoryId ?? null,
              })
            )}
          onMerged={() => setSelectedIds(new Set())}
        />
      )}
    </div>
  );
}
